import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileDown, Sparkles, Lock, ArrowLeft, BookOpenCheck, CheckCircle2, Clock } from "lucide-react";

function WorksheetCard({ ws }) {
  const isLocked = ws.is_premium; // future monetisation gate — free for everyone right now
  return (
    <div
      className="rounded-lg border border-white/10 bg-[#152040] p-5 hover:border-orange-300/40 transition-colors"
      data-testid={`worksheet-card-${ws.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <BookOpenCheck className="w-4 h-4 text-yellow-300 shrink-0" />
          <div className="font-display text-base font-bold text-zinc-100 truncate">{ws.title}</div>
        </div>
        {ws.is_premium ? (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-orange-300/10 text-orange-300 border border-orange-300/30 flex items-center gap-1 shrink-0">
            <Sparkles className="w-3 h-3" /> Premium
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-300/10 text-emerald-300 border border-emerald-300/30 shrink-0">
            Free
          </span>
        )}
      </div>
      {ws.description && (
        <p className="text-sm text-zinc-400 mb-4 leading-relaxed">{ws.description}</p>
      )}
      <div className="flex flex-wrap gap-2">
        <a
          href={ws.worksheet_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`worksheet-download-${ws.id}`}
          className={`inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 transition-colors ${
            isLocked
              ? "bg-white/5 text-zinc-500 cursor-not-allowed pointer-events-none"
              : "bg-gradient-to-r from-pink-300 to-orange-300 text-slate-900 hover:from-pink-200 hover:to-orange-200"
          }`}
          onClick={(e) => {
            if (isLocked) e.preventDefault();
          }}
        >
          {isLocked ? <Lock className="w-4 h-4" /> : <FileDown className="w-4 h-4" />}
          Download worksheet
        </a>
        {ws.solution_url ? (
          <a
            href={ws.solution_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`solution-download-${ws.id}`}
            className="inline-flex items-center gap-2 text-sm font-medium rounded-full px-4 py-2 border border-white/10 text-zinc-200 hover:border-yellow-300/50 hover:text-yellow-300 transition-colors"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            Solutions
          </a>
        ) : (
          <span
            className="inline-flex items-center gap-2 text-sm text-zinc-500 border border-dashed border-white/10 rounded-full px-4 py-2"
            data-testid={`solution-pending-${ws.id}`}
          >
            <Clock className="w-4 h-4" />
            Solutions coming soon
          </span>
        )}
      </div>
    </div>
  );
}

export default function AdvancedWorksheets() {
  const { classId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get(`/worksheets/${classId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.status || "error"));
  }, [classId]);

  if (err) {
    return (
      <div className="text-zinc-400" data-testid="worksheets-error">
        Couldn't load worksheets for this class.
      </div>
    );
  }
  if (!data) return <div className="text-zinc-400">Loading worksheets…</div>;

  return (
    <div className="fade-up">
      <Link
        to={`/classes/${classId}`}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-yellow-300 mb-4"
        data-testid="back-to-class"
      >
        <ArrowLeft className="w-4 h-4" /> Back to {data.class_label}
      </Link>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">
            {data.class_label} · Advanced Practice
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
            Advanced Practice Worksheets
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Downloadable worksheet packs curated for exam-level practice. Print, solve, and check
            against the solutions once available.
          </p>
        </div>
        <div
          className="text-[11px] uppercase tracking-[0.2em] text-emerald-300 border border-emerald-300/30 rounded-full px-3 py-1"
          data-testid="worksheets-free-badge"
        >
          Free for now · Premium coming soon
        </div>
      </div>

      {data.worksheets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 bg-[#0f1a34] p-8 text-center text-zinc-500 text-sm">
          No advanced worksheets uploaded yet for this class.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="worksheets-grid">
          {data.worksheets.map((w) => (
            <WorksheetCard key={w.id} ws={w} />
          ))}
        </div>
      )}

      <div className="mt-8 rounded-lg border border-white/5 bg-white/[0.02] p-4 text-xs text-zinc-500">
        <div className="flex items-center gap-2 mb-1 text-zinc-300">
          <Sparkles className="w-3.5 h-3.5 text-orange-300" />
          <span className="font-semibold">About these worksheets</span>
        </div>
        Each series contains 5 sheets. Files open in a new tab as Word documents — you can print
        directly or edit in a doc editor. Solutions will be added by our teachers over the next
        weeks{user?.is_admin && ", and admins can upload them via the admin console"}.
      </div>
    </div>
  );
}
